/* eslint-disable no-multi-str */
import React, { createContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import moment from 'moment';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { GET_SERVICE_LIST } from '../../../network/queries';
import { SUBMIT_BOOKING } from '../../../network/mutations';

export const Context = createContext({});

const footerText = {
  footerHeading: 'my bookings',
  footerParagraph: 'update, cancel or reschedule time',
};

const DummyQuestions = [
  {
    label:
      'Have you had close contact with anyone who has COVID-19 in the last 14 days? Close contact means living int the same household as a person who has tested positive for COVID-19 for 15 minutes or more, or coming in direct contact with secretions (e.g., sharing utensils, being coughed on) from a person who has tested positive for COVID-19 while that person was symptomatic?',
    name: 'question_1',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
  },
  {
    label: 'Do you have a fever of 100°F or above?',
    name: 'question_2',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
  },

  {
    label:
      'Have you had any of the following symptoms in the past 10 days - not due to any other cause diagnosed by a healthcare provider: Fever or chills; dry cough or difficulty breathing; nausea or vomiting; diarrhea; muscle or body aches; fatigue; headache; sore throat; new loss of taste or smell; congestion or runny nose?',
    name: 'question_3',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
  },
  {
    label: 'Have you used medication to reduce a fever in the last 72 hours?',
    name: 'question_4',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
  },
  {
    label: 'Have you received your FIRST positive test or COVID-19 diagnosis in the last 10 days?',
    name: 'question_5',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
  },
];

const bookingsPage = 'http://localhost:3000/m/:virtualPhoneNumber';

export const Provider = ({ virtualPhoneNumber, serviceId, children }) => {
  const { loading: queryLoading, error: queryError, data: queryData } = useQuery(GET_SERVICE_LIST, {
    variables: {
      vpn: virtualPhoneNumber,
    },
  });

  const billingSchema = yup.object().shape({
    cardNumber: yup.string().required('Oops! Card number can`t be blank'),
    expiryDate: yup.string().required('Oops! Expiry can`t be blank'),
    cvc: yup.string().required('Oops! CVC code can`t be blank'),
  });

  const schema = yup.object().shape({
    firstName: yup.string().required('Oops! First Name can`t be blank.'),
    lastName: yup.string().required('Oops! Last Name can`t be blank.'),
    primaryPhoneNumber: yup.mixed().test({
      name: 'primaryPhoneNumber',
      message: 'Phone number should have 9 characters or more',
      test: (value) => isValidPhoneNumber(value),
    }),
  });

  const questionsErrorMessage = 'We are sorry to hear that you or others \
    are in contact with are not feeling well. \
    Please refer to your state or federal Covid \
    guidelines for recommended steps. And please \
    reschedule after the proper quarantine, so we \
    can all stay safe and healthy.';

  const questionsSchema = yup.object().shape({
    question_1: yup
      .string()
      .required('Oops! Field can`t be blank.')
      .test('no', questionsErrorMessage, (value) => value === 'no'),
    question_2: yup
      .string()
      .required('Oops! Field can`t be blank.')
      .test('no', questionsErrorMessage, (value) => value === 'no'),
    question_3: yup
      .string()
      .required('Oops! Field can`t be blank.')
      .test('no', questionsErrorMessage, (value) => value === 'no'),
    question_4: yup
      .string()
      .required('Oops! Field can`t be blank.')
      .test('no', questionsErrorMessage, (value) => value === 'no'),
    question_5: yup
      .string()
      .required('Oops! Field can`t be blank.')
      .test('no', questionsErrorMessage, (value) => value === 'no'),
  });

  const [validationError, setValidationError] = useState([]);
  const [validationBillingError, setValidationBillingError] = useState([]);
  const [usedFields, setUsedFields] = useState([]);
  const [validationQuestionErrors, setValidationQuestionErrors] = useState([]);

  const [step, setStep] = useState(0);
  const [lastStep, setLastStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    primaryPhoneNumber: '',
  });
  const [selectedService, setSelectedService] = useState({});
  const [questionsData, setQuestionsData] = useState({});

  const goToNextStep = () => {
    const nextStep = lastStep === step ? lastStep + 1 : lastStep;
    setLastStep(nextStep);
    setStep(nextStep);
  };

  const setField = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const selectServiceById = (sId) => {
    const service = queryData.getVpn.availableServices.find((e) => e.id === sId);
    if (service) {
      setField('serviceId', sId);
      setSelectedService(service);
      setStep(1);
      setLastStep(1);
    }
  };

  useEffect(() => {
    window.onpopstate = (e) => {
      e.preventDefault();
      window.history.go(1);
    };
  }, []);

  useEffect(() => {
    if (queryData && serviceId && serviceId !== '') {
      selectServiceById(serviceId);
      setStep(1);
      setLastStep(1);
    }
  }, [queryData]);

  const selectServiceDate = (date) => {
    const { serviceDate, serviceInterval } = formData;
    setFormData({
      ...formData,
      serviceDay: date,
      serviceInterval: serviceDate === date ? serviceInterval : null,
    });
  };

  const selectEventTime = (interval) => {
    setFormData({
      ...formData,
      serviceInterval: interval,
    });
  };

  // ====================================
  // Validation

  const handleFormValidation = (formFields) => {
    schema.validate(formFields, { abortEarly: false }).then(
      () => {
        setValidationError([]);
      },
      (response) => {
        setValidationError(response.inner);
      },
    );
  };

  const handleBillingValidations = (formFields) => {
    billingSchema.validate(formFields, { abortEarly: false }).then(
      () => {
        setValidationBillingError([]);
      },
      (response) => {
        setValidationBillingError(response.inner);
      },
    );
  };

  const handleQuestionsValidation = (formFields) => {
    questionsSchema.validate(formFields, { abortEarly: false }).then(
      () => {
        setValidationQuestionErrors([]);
      },
      (response) => {
        setValidationQuestionErrors(response.inner);
      },
    );
  };

  const updateUsedFields = (name) => {
    if (!usedFields.includes(name)) {
      usedFields.push(name);
      setUsedFields(usedFields);
      handleFormValidation(formData);
      handleBillingValidations(formData);
      handleQuestionsValidation(questionsData);
    }
  };

  const setQuestionAnswer = (name, value) => {
    const newQuestionsData = { ...questionsData, [name]: value };
    setQuestionsData(newQuestionsData);
    handleQuestionsValidation(newQuestionsData);
  };

  const hasErrorMessage = (name) => {
    const messages = [];
    validationError.forEach((e) => {
      if (e.path === name && usedFields.includes(name)) {
        messages.push(e.message);
      }
    });
    return messages;
  };

  useEffect(() => {
    handleFormValidation({ ...formData });
  }, [formData]);

  const [createInstantBooking, { loading: mutationLoading, error: mutationError }] = useMutation(
    SUBMIT_BOOKING,
  );

  const submitBookingData = () => {
    const {
      firstName, lastName, primaryPhoneNumber, serviceDay, serviceInterval,
    } = formData;

    const sDate = moment(serviceDay);
    const sInterval = moment(serviceInterval, 'hh:mm:ss');

    sDate.hours(sInterval.hours());
    sDate.minutes(sInterval.minutes());
    sDate.utc(true);

    const inputData = {
      firstName,
      lastName,
      primaryPhoneNumber,
      serviceId: selectedService.id,
      timeSlot: new Date(sDate),
    };

    return new Promise((resolve, reject) => {
      createInstantBooking({ variables: { input: inputData } }).then(
        (res) => resolve(res),
        (err) => reject(err),
      );
    });
  };

  const contextData = {
    footerQuestions: footerText,
    bookingsPage,
    formData,
    setField,
    updateUsedFields,
    selectedService,
    selectServiceById,
    selectServiceDate,
    selectEventTime,
    currentStep: step,
    goToStep: setStep,
    goToNextStep,
    mutationLoading,
    mutationError,
    queryLoading,
    queryError,
    queryData,
    validationError,
    isFormValid: validationError.length === 0,
    isBillingFormValid: validationBillingError.length === 0,
    validationBillingError,
    isQuestionsFormValid: validationQuestionErrors.length === 0,
    validationQuestionErrors,
    hasErrorMessage,
    questions: DummyQuestions,
    questionsData,
    setQuestionAnswer,
    submitBookingData,
  };

  return <Context.Provider value={contextData}>{children}</Context.Provider>;
};

export const { Consumer } = Context;

Provider.defaultProps = {
  serviceId: '',
};

Provider.propTypes = {
  virtualPhoneNumber: PropTypes.string.isRequired,
  serviceId: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
